import { useState, createContext, useContext, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RocketIcon, CalendarIcon } from "lucide-react";

interface ComingSoonModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: string;
  description?: string;
}

export function ComingSoonModal({ isOpen, onClose, feature, description }: ComingSoonModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <RocketIcon className="h-8 w-8 text-white" />
          </div>
          <DialogTitle className="text-2xl font-bold">
            {feature} Coming Soon!
          </DialogTitle>
          <DialogDescription className="text-base space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                <CalendarIcon className="h-3 w-3 mr-1" />
                Version 2.0
              </Badge>
            </div>
            <p>
              {description || `We're working hard to bring you ${feature.toLowerCase()} functionality. This feature will be available in our next major release.`}
            </p>
            <p className="text-sm text-muted-foreground">
              Stay tuned for updates and new features!
            </p>
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center pt-4">
          <Button onClick={onClose} className="w-full sm:w-auto">
            Got it, thanks!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Global Context for Coming Soon Modal
interface ComingSoonContextType {
  isOpen: boolean;
  feature: string;
  description: string;
  showComingSoon: (featureName: string, desc?: string) => void;
  hideComingSoon: () => void;
}

const ComingSoonContext = createContext<ComingSoonContextType | undefined>(undefined);

// Global Provider Component
export function ComingSoonProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [feature, setFeature] = useState('');
  const [description, setDescription] = useState('');

  const showComingSoon = (featureName: string, desc?: string) => {
    setFeature(featureName);
    setDescription(desc || '');
    setIsOpen(true);
  };

  const hideComingSoon = () => {
    setIsOpen(false);
    // Clear data after animation completes
    setTimeout(() => {
      setFeature('');
      setDescription('');
    }, 200);
  };

  return (
    <ComingSoonContext.Provider value={{
      isOpen,
      feature,
      description,
      showComingSoon,
      hideComingSoon,
    }}>
      {children}
      {/* Single global modal instance */}
      <ComingSoonModal 
        isOpen={isOpen}
        onClose={hideComingSoon}
        feature={feature}
        description={description}
      />
    </ComingSoonContext.Provider>
  );
}

// Hook for easy usage - now uses global context
export function useComingSoon() {
  const context = useContext(ComingSoonContext);
  if (context === undefined) {
    throw new Error('useComingSoon must be used within a ComingSoonProvider');
  }
  return context;
} 